import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ARTICLES } from "@/lib/blog";
import { Reveal, ParallaxImage } from "@/components/motion-primitives";
import { CtaBand, usePageMeta } from "@/components/shared";

export default function BlogPost() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  usePageMeta(article?.title, article?.excerpt);

  useEffect(() => {
    if (!article) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: article.image,
      author: { "@type": "Organization", name: "The Bermuda Co-Host" },
      publisher: { "@type": "Organization", name: "The Bermuda Co-Host" },
    });
    document.head.appendChild(s);
    return () => s.remove();
  }, [article]);

  if (!article) return <Navigate to="/resources" replace />;

  return (
    <main>
      <article>
        <header className="relative flex min-h-[60vh] items-end overflow-hidden bg-navy text-ivory">
          <div className="absolute inset-0">
            <ParallaxImage src={article.image} alt={article.title} className="h-full" speed={0.08} />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-navy/30" />
          </div>
          <div className="relative z-10 mx-auto w-full max-w-[900px] px-6 pb-16 pt-40">
            <Reveal>
              <Link to="/resources" data-testid="article-back-link" className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-ivory/70 hover:text-bermuda transition-colors">
                <ArrowLeft size={13} /> Resource Center
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.12] tracking-tight">{article.title}</h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 text-[11px] uppercase tracking-[0.25em] text-ivory/60">{article.date} · {article.readTime}</p>
            </Reveal>
          </div>
        </header>

        <div className="mx-auto max-w-[760px] px-6 py-16 lg:py-24" data-testid="article-body">
          {article.sections.map((section, i) => (
            <Reveal key={i} delay={0.02}>
              <section className="mb-12">
                <h2 className="font-serif text-2xl sm:text-[28px] leading-snug text-navy">{section.heading}</h2>
                {section.paragraphs.map((p, j) => (
                  <p key={j} className="mt-5 text-[15px] leading-[1.85] text-navy/70">{p}</p>
                ))}
              </section>
            </Reveal>
          ))}
          <Reveal>
            <div className="mt-4 border-l-2 border-bermuda bg-sand-light p-8">
              <p className="font-serif text-xl leading-relaxed text-navy/85">
                Caring for a Bermuda property from near or far?{" "}
                <Link to="/assessment" data-testid="article-cta-link" className="text-bermuda-dark underline underline-offset-4 hover:text-navy">
                  Request a property assessment
                </Link>{" "}
                — honest, specific and without obligation.
              </p>
            </div>
          </Reveal>
        </div>
      </article>
      <CtaBand />
    </main>
  );
}
